/* eslint-disable max-lines */
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil, tap } from 'rxjs/operators';

import { TmpI18NService } from 'app/i18n/tmp-i18n.service';
import { CompanyService } from 'app/shared/service/company.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';

import { IDomainModel, IJointVentureDomainInputs } from './domain.model';
import { ICompanyHomeSettings } from '../../company-home-settings.model';

@Component({
  selector: 'cc-company-domain-settings',
  templateUrl: './company-domain-settings.component.html',
  styleUrls: [
    '../company-general-settings/company-general-settings.component.scss',
    './company-domain-settings.component.scss',
  ],
})
export class CompanyDomainSettingsComponent implements AfterViewInit, OnDestroy {
  @Input() domainModel: IDomainModel;
  @Input() isSuperAdmin: boolean;

  @Input() model: ICompanyHomeSettings;

  @ViewChildren('domainInputRef') domainInputRef: QueryList<ElementRef>;

  hasDomainError: boolean;

  private unsubscribe = new Subject<void>();

  // Accepts single domain that includes .com or equivalent + no spaces
  readonly domainRegex =
    /^((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  constructor(
    private readonly i18nService: TmpI18NService,
    private readonly companyService: CompanyService,
    private readonly errorHandler: ErrorHandler,
  ) {}

  ngAfterViewInit() {
    this.setUpListenerForDomainChange();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  setUpListenerForDomainChange() {
    this.domainInputRef.forEach((element) => {
      fromEvent(element.nativeElement, 'keyup')
        .pipe(
          takeUntil(this.unsubscribe),
          filter(Boolean),
          debounceTime(200),
          distinctUntilChanged(),
          tap(() => {
            this.validateDomains();
          }),
        )
        .subscribe();
    });
  }

  onJointVentureToggleClicked(event: Event) {
    if (!this.isSuperAdmin) {
      event.preventDefault();
    }
  }

  onJointVentureChange() {
    if (this.model.jointVenture) {
      this.domainModel.jointVentureDomainInput = this.domainModel.domainInput;
      this.domainModel.parentDomainInput = this.domainModel.parentDomainInput || '';
    } else {
      this.domainModel.domainInput = this.domainModel.jointVentureDomainInput;
    }

    this.validateDomains();
  }

  validateDomains() {
    if (this.model.jointVenture) {
      this.domainModel.domainInputError = '';
      this.validateForJointVenture();
      this.validateForParentDomain();
    } else {
      this.domainModel.jointVentureDomainInputError = '';
      this.domainModel.parentDomainInputError = '';
      this.validateForNonJointVenture();
    }
  }

  private validateForNonJointVenture() {
    if (!this.domainModel.domainInput) {
      this.domainModel.isMissingDomain = true;
      this.domainModel.domainInputError = '';
      return;
    }

    this.domainModel.isMissingDomain = false;
    const domains = this.companyService.getFormattedDomainInput(this.domainModel.domainInput);

    this.hasDomainError = [
      this.validateSingleDomainFormat.bind(this),
      this.validateForPublicDomains.bind(this),
    ].some((validator) => validator(domains));
  }

  private validateSingleDomainFormat(domains: string[]) {
    const isInvalidRegex = domains.some((domain: string) => !this.domainRegex.test(domain));
    if (isInvalidRegex) {
      this.domainModel.domainInputError = this.i18nService.getMessage('invalidDomain');
    }

    return isInvalidRegex;
  }

  private validateForPublicDomains(domains: string[]) {
    this.companyService
      .validatePublicDomains(domains)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        () => {
          this.domainModel.domainInputError = '';
        },
        () => {
          this.domainModel.domainInputError = this.i18nService.getMessage('invalidPublicDomains');
        },
      );
  }

  // Joint Venture Domain

  private validateForJointVenture() {
    const jointVentureDomains = this.companyService.getFormattedDomainInput(
      this.domainModel.jointVentureDomainInput,
    );
    const parentDomains = this.companyService.getFormattedDomainInput(this.domainModel.parentDomainInput);

    this.hasDomainError = [
      this.jointVentureDomainsNotDefined.bind(this),
      this.invalidJvDomainsFormat.bind(this),
      this.jvDomainCannotBeOneOfTheParentDomains.bind(this),
      this.jvAndParentDomainsCannotBeTheSame.bind(this),
      this.validateJointVentureForPublicDomains.bind(this),
    ].some((validator) => validator({ jointVentureDomains, parentDomains }));
  }

  private jointVentureDomainsNotDefined({ jointVentureDomains }: IJointVentureDomainInputs) {
    if (!jointVentureDomains[0]) {
      this.domainModel.jointVentureDomainInputError = this.i18nService.getMessage('jointVentureDomainNotSet');
      return true;
    }
    return false;
  }

  private invalidJvDomainsFormat({ jointVentureDomains }: IJointVentureDomainInputs) {
    if (jointVentureDomains.some((domain: string) => !this.domainRegex.test(domain))) {
      this.domainModel.jointVentureDomainInputError = this.i18nService.getMessage('invalidDomain');
      return true;
    }
    return false;
  }

  private jvDomainCannotBeOneOfTheParentDomains({
    jointVentureDomains,
    parentDomains,
  }: IJointVentureDomainInputs) {
    const combinedDomains = [...parentDomains, ...jointVentureDomains];
    const uniqueDomains = [...new Set(combinedDomains)];
    if (uniqueDomains.length !== combinedDomains.length) {
      this.domainModel.jointVentureDomainInputError = this.i18nService.getMessage(
        'jvDomainCannotBeOneOfTheParentDomains',
      );
      return true;
    }

    return false;
  }

  private validateJointVentureForPublicDomains({ jointVentureDomains }: IJointVentureDomainInputs) {
    this.companyService
      .validatePublicDomains(jointVentureDomains)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        () => {
          this.domainModel.jointVentureDomainInputError = '';
        },
        (error) => {
          const publicDomains = error?.error?.publicDomains;
          if (!publicDomains) {
            this.errorHandler.handle(error);
          } else {
            this.domainModel.jointVentureDomainInputError =
              this.i18nService.getMessage('invalidPublicDomains');
          }
        },
      );
  }

  // Parent Domain

  private validateForParentDomain() {
    const jointVentureDomains = this.companyService.getFormattedDomainInput(
      this.domainModel.jointVentureDomainInput,
    );
    const parentDomains = this.companyService.getFormattedDomainInput(this.domainModel.parentDomainInput);

    this.hasDomainError = [
      this.jvAndParentDomainsCannotBeTheSame.bind(this),
      this.lessThanTwoParentDomains.bind(this),
      this.validateParentDomainsFormat.bind(this),
      this.validateParentForPublicDomains.bind(this),
    ].some((validator) => validator({ jointVentureDomains, parentDomains }));
  }

  private validateParentDomainsFormat({ parentDomains }: IJointVentureDomainInputs) {
    if (parentDomains.some((domain: string) => !this.domainRegex.test(domain))) {
      this.domainModel.parentDomainInputError = this.i18nService.getMessage('invalidDomain');
      return true;
    }

    return false;
  }

  private lessThanTwoParentDomains({ parentDomains }: IJointVentureDomainInputs) {
    if (parentDomains.length < 2) {
      this.domainModel.parentDomainInputError = this.i18nService.getMessage('atLeastTwoParentDomains');
      return true;
    }
    return false;
  }

  private jvAndParentDomainsCannotBeTheSame({
    jointVentureDomains,
    parentDomains,
  }: IJointVentureDomainInputs) {
    const domainsAreTheSame =
      jointVentureDomains.length === 1 &&
      parentDomains.length === 1 &&
      parentDomains[0] === jointVentureDomains[0];

    if (domainsAreTheSame) {
      this.domainModel.parentDomainInputError = this.i18nService.getMessage('parentDomainsCannotBeSameAsJv');
      return true;
    }
    return false;
  }

  private validateParentForPublicDomains({ parentDomains }: IJointVentureDomainInputs) {
    this.companyService
      .validatePublicDomains(parentDomains)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        () => {
          this.domainModel.parentDomainInputError = '';
        },
        (error) => {
          const publicDomains = error?.error?.publicDomains;
          if (!publicDomains) {
            this.errorHandler.handle(error);
          } else {
            this.domainModel.parentDomainInputError = this.i18nService.getMessage('invalidPublicDomains');
          }
        },
      );
  }
}
